/**
 * Reference events table with "Load more" (pages by `before` = created_at of the last row)
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { ReferenceEvent, Severity } from '../types/references';

const SEVERITY_COLORS: Record<Severity, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

// Events without a device are network events (e.g. a handover)
const deviceLabel = (e: ReferenceEvent) => (e.device_id === null ? 'network' : e.device_key ?? String(e.device_id));

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { hour12: false });
};

interface EventsListProps {
  // Loads one page: newest first, older than `before` when given
  load: (before?: string) => Promise<ReferenceEvent[]>;
  pageSize: number;
}

const EventsList: React.FC<EventsListProps> = ({ load, pageSize }) => {
  const [events, setEvents] = useState<ReferenceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (before?: string) => {
      setLoading(true);
      try {
        const page = await load(before);
        setEvents((prev) => (before ? [...prev, ...page] : page));
        setHasMore(page.length >= pageSize);
        setError(null);
      } catch (e) {
        setError('Failed to load reference events');
      } finally {
        setLoading(false);
      }
    },
    [load, pageSize]
  );

  useEffect(() => {
    fetchPage(undefined);
  }, [fetchPage]);

  const last = events[events.length - 1];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && events.length === 0 && !error ? (
        <Typography variant="body2" color="text.secondary">
          No reference events
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Time', 'Device', 'Reference', 'From → To', 'Reason'].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((e, i) => (
              <TableRow key={e.id ?? `${e.created_at}-${i}`}>
                <TableCell>{formatTime(e.created_at)}</TableCell>
                <TableCell>{deviceLabel(e)}</TableCell>
                <TableCell>{`${e.kind} · ${e.subject}`}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={SEVERITY_COLORS[e.severity] ?? 'info'}
                    label={`${e.from_state ?? '—'} → ${e.to_state ?? '—'}`}
                  />
                </TableCell>
                <TableCell>{e.reason ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading && <CircularProgress size={20} />}
        {!loading && hasMore && last && (
          <Button size="small" onClick={() => fetchPage(last.created_at)}>
            Load more
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EventsList;
